import { useEffect, useMemo, useState } from "react"

//api
import { deletePasskey, listMyPasskeys } from  "@/api/orgSettings/passkeys";

//services
import { GrantTypeService } from "@/services/grantType.service";

//helpers
import { formatDateToDDMMYYYYHHMM } from "@/helpers/Dates";
import { useTimer } from "@/components/hooks/utility/useTimer";
import { getAddPasskeyLink, getSignInWithDeviceLink } from "@/helpers/OrgSettings";

//interfaces
import { NotificationStatus } from "@/interfaces/languages";
import { IPasskey } from "@/interfaces/passkeys";

//components
import { ISimpleTableColumn, ISimpleTableProps, ISimpleTableRows } from "thrive-components";
import { logoutOfCMS } from "@/helpers/Auth";





const UseManageYourPasskeysPage = () => {  
    const emptyPasskeysMessage = "No items to display";
    const addPasskeyLink = getAddPasskeyLink();
    const signInWithDeviceCodeLink = getSignInWithDeviceLink();
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus | null, message: string}>();
    const [selectedPasskey, setSelectedPasskey] = useState<IPasskey | null>(null);
    const [passkeyData, setPasskeyData] = useState<IPasskey[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [morePasskeysAvailable, setMorePasskeysAvailable] = useState<boolean>(false);
    const [skip, setSkip] = useState<number>(0);
    const [hasPasskeyButIsntUsingIt, setHasPasskeyButIsntUsingIt] = useState<boolean>(false);

    const columns: Array<ISimpleTableColumn> = [
        { title: "Name", isToggleButton: false }, 
        { title: "Date Added", isToggleButton: false }, 
        { title: "Last Used", isToggleButton: false }, 
    ];
    const tableData = useMemo<ISimpleTableProps>(() => (
        {
            simpleTableContent: {columns: columns, rows: formatTableRows(hasPasskeyButIsntUsingIt)},
            isInfiniteScroll: true,
            isLoading: isLoading,
            noDataMessage: emptyPasskeysMessage,
            getMoreContent: getMorePasskeys,
            infiniteScrollFetchCondition: morePasskeysAvailable,
        }), [ passkeyData, isLoading, hasPasskeyButIsntUsingIt]
    );

    useEffect(() => {
      getMyPasskeys();
    }, [])

    useTimer(notificationDetails?.show ?? false, ()=>setNotificationDetails({show: false, status:null, message: ""}), 3000);

    function formatTableRows(hideControls: boolean){
        return passkeyData.map((passkey) => ({
            content: [
                passkey.name,
                formatDateToDDMMYYYYHHMM(passkey.dateCreated),
                passkey.lastUsed ? formatDateToDDMMYYYYHHMM(passkey.lastUsed) : " "
            ],
            controls: hideControls
                ? []
                : [ {content:"Remove", onClick:() => setSelectedPasskey(passkey) }]   
        })) as ISimpleTableRows[]
    }

    function getMyPasskeys(isPagination?: boolean) {
        
        let passkeyCount = passkeyData?.length ?? 0;
        setIsLoading(true)
        listMyPasskeys(skip)
        .then(({items, nextSkip, totalItemCount}) => {
            if (isPagination) {
                setPasskeyData((prevDraftData) => [...(prevDraftData || []), ...items]);
            } else {
                setPasskeyData(items);
            }
            setSkip(nextSkip);
            setMorePasskeysAvailable((passkeyCount + (items?.length ?? 0)) < totalItemCount);
            setHasPasskeyButIsntUsingIt(totalItemCount > 0 && !GrantTypeService.isMfaSession )
        })
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message: "Unable to fetch passkeys at this time. Please try again."});
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    function getMorePasskeys() {
        getMyPasskeys(true);
    }

    function onRemovePasskeyClick() {
        if (selectedPasskey) {
            deletePasskey(selectedPasskey.id)
            .then(() => {
                setNotificationDetails({ show: true, status: NotificationStatus.success, message: "Successfully removed passkey."});
                if (selectedPasskey.isLoggedInCredential) {
                    logoutOfCMS();
                }
            })
            .catch(() => {
                setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Something went wrong. Please try again."});
            })
            .finally(() => {
                setSelectedPasskey(null);
                getMyPasskeys();
            })
        }
    }
    return {addPasskeyLink, selectedPasskey, setSelectedPasskey, tableData, notificationDetails, onRemovePasskeyClick, hasPasskeyButIsntUsingIt, signInWithDeviceCodeLink}
}
export default UseManageYourPasskeysPage